import { useState } from "react";
import {
  Plus,
  Search,
  MoreHorizontal,
  Trash2,
  Edit3,
  Settings,
  PanelLeftClose,
  PanelLeft,
  Star,
  Archive,
  Share,
  Download,
  MessageSquare,
  HelpCircle,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import { ZeusBolt } from "@/components/ZeusBolt";
import { type Conversation } from "@/hooks/useChat";

interface ChatSidebarProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onPin: (id: string) => void;
  onArchive?: (id: string) => void;
  collapsed: boolean;
  onToggle: () => void;
  onSignOut?: () => void;
}

export function ChatSidebar({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  onRename,
  onPin,
  onArchive,
  collapsed,
  onToggle,
  onSignOut,
}: ChatSidebarProps) {
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);

  const filtered = conversations.filter(
    (c) => !c.archived && c.title.toLowerCase().includes(search.toLowerCase())
  );

  const starred = filtered.filter((c) => c.pinned);
  const unstarred = filtered.filter((c) => !c.pinned);

  const groupByDate = (convs: Conversation[]) => {
    const now = new Date();
    const today: Conversation[] = [];
    const yesterday: Conversation[] = [];
    const week: Conversation[] = [];
    const month: Conversation[] = [];
    const older: Conversation[] = [];

    convs.forEach((c) => {
      const diff = (now.getTime() - c.updatedAt.getTime()) / (1000 * 60 * 60 * 24);
      if (diff < 1) today.push(c);
      else if (diff < 2) yesterday.push(c);
      else if (diff < 7) week.push(c);
      else if (diff < 30) month.push(c);
      else older.push(c);
    });

    return { today, yesterday, week, month, older };
  };

  const groups = groupByDate(unstarred);

  const handleRename = (id: string) => {
    if (renameValue.trim()) {
      onRename(id, renameValue.trim());
    }
    setRenaming(null);
  };

  /* ── Collapsed state ── */
  if (collapsed) {
    return (
      <div className="flex flex-col items-center py-3 gap-2 w-[52px] bg-[hsl(var(--sidebar-background))] border-r border-border/40 h-screen">
        <button
          onClick={onToggle}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          aria-label="Expand sidebar"
        >
          <PanelLeft className="h-[18px] w-[18px]" />
        </button>
        <button
          onClick={onNew}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
          aria-label="New chat"
        >
          <Plus className="h-[18px] w-[18px]" />
        </button>
      </div>
    );
  }

  /* ── Expanded state ── */
  return (
    <div className="flex flex-col w-[260px] bg-[hsl(var(--sidebar-background))] border-r border-border/40 h-screen select-none">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-3 h-[52px] shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={onToggle}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="h-[18px] w-[18px]" />
          </button>
        </div>
        <div className="flex items-center gap-0.5">
          {searchOpen ? (
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => { if (!search) setSearchOpen(false); }}
              onKeyDown={(e) => { if (e.key === "Escape") { setSearch(""); setSearchOpen(false); }}}
              placeholder="Search…"
              className="w-[140px] px-2.5 py-1.5 rounded-lg bg-secondary text-[13px] text-foreground placeholder:text-muted-foreground border border-border/60 outline-none focus:ring-1 focus:ring-ring/40"
            />
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
              aria-label="Search chats"
            >
              <Search className="h-[18px] w-[18px]" />
            </button>
          )}
          <button
            onClick={onNew}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="New chat"
          >
            <Plus className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>

      {/* ── Chat list ── */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-1.5 pt-0.5 pb-2">
        {starred.length > 0 && (
          <Section label="Starred">
            {starred.map((c) => (
              <ChatItem
                key={c.id}
                conv={c}
                active={activeId === c.id}
                onSelect={onSelect}
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                renaming={renaming}
                setRenaming={setRenaming}
                renameValue={renameValue}
                setRenameValue={setRenameValue}
                onRename={handleRename}
                onDelete={onDelete}
                onPin={onPin}
                onArchive={onArchive}
              />
            ))}
          </Section>
        )}
        {groups.today.length > 0 && (
          <Section label="Today">
            {groups.today.map((c) => <ChatItem key={c.id} conv={c} active={activeId === c.id} onSelect={onSelect} menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming} renameValue={renameValue} setRenameValue={setRenameValue} onRename={handleRename} onDelete={onDelete} onPin={onPin} onArchive={onArchive} />)}
          </Section>
        )}
        {groups.yesterday.length > 0 && (
          <Section label="Yesterday">
            {groups.yesterday.map((c) => <ChatItem key={c.id} conv={c} active={activeId === c.id} onSelect={onSelect} menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming} renameValue={renameValue} setRenameValue={setRenameValue} onRename={handleRename} onDelete={onDelete} onPin={onPin} onArchive={onArchive} />)}
          </Section>
        )}
        {groups.week.length > 0 && (
          <Section label="Previous 7 days">
            {groups.week.map((c) => <ChatItem key={c.id} conv={c} active={activeId === c.id} onSelect={onSelect} menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming} renameValue={renameValue} setRenameValue={setRenameValue} onRename={handleRename} onDelete={onDelete} onPin={onPin} onArchive={onArchive} />)}
          </Section>
        )}
        {groups.month.length > 0 && (
          <Section label="Previous 30 days">
            {groups.month.map((c) => <ChatItem key={c.id} conv={c} active={activeId === c.id} onSelect={onSelect} menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming} renameValue={renameValue} setRenameValue={setRenameValue} onRename={handleRename} onDelete={onDelete} onPin={onPin} onArchive={onArchive} />)}
          </Section>
        )}
        {groups.older.length > 0 && (
          <Section label="Older">
            {groups.older.map((c) => <ChatItem key={c.id} conv={c} active={activeId === c.id} onSelect={onSelect} menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming} renameValue={renameValue} setRenameValue={setRenameValue} onRename={handleRename} onDelete={onDelete} onPin={onPin} onArchive={onArchive} />)}
          </Section>
        )}
        {filtered.length === 0 && conversations.length > 0 && (
          <p className="text-[12px] text-muted-foreground text-center py-10">No chats found</p>
        )}
        {conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-[12px] text-muted-foreground">No conversations yet</p>
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="px-1.5 py-1.5 border-t border-border/40 shrink-0 space-y-0.5">
        <FooterButton icon={Settings} label="Settings" />
        <FooterButton icon={HelpCircle} label="Help & FAQ" />
        {onSignOut && (
          <FooterButton icon={LogOut} label="Sign out" onClick={onSignOut} />
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mt-1 mb-0.5">
      <p className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider px-2.5 pt-3 pb-1.5 select-none">
        {label}
      </p>
      <div className="space-y-px">{children}</div>
    </div>
  );
}

function FooterButton({ icon: Icon, label, onClick }: { icon: React.ElementType; label: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

interface ChatItemProps {
  conv: Conversation;
  active: boolean;
  onSelect: (id: string) => void;
  menuOpen: string | null;
  setMenuOpen: (id: string | null) => void;
  renaming: string | null;
  setRenaming: (id: string | null) => void;
  renameValue: string;
  setRenameValue: (v: string) => void;
  onRename: (id: string) => void;
  onDelete: (id: string) => void;
  onPin: (id: string) => void;
  onArchive?: (id: string) => void;
}

function ChatItem({
  conv,
  active,
  onSelect,
  menuOpen,
  setMenuOpen,
  renaming,
  setRenaming,
  renameValue,
  setRenameValue,
  onRename,
  onDelete,
  onPin,
  onArchive,
}: ChatItemProps) {
  const isMenuOpen = menuOpen === conv.id;

  if (renaming === conv.id) {
    return (
      <div className="px-1.5 py-0.5">
        <input
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={() => onRename(conv.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onRename(conv.id);
            if (e.key === "Escape") setRenaming(null);
          }}
          className="w-full px-2.5 py-1.5 rounded-lg bg-secondary text-[13px] text-foreground border border-border outline-none focus:ring-1 focus:ring-ring/40"
        />
      </div>
    );
  }

  return (
    <div className="relative group">
      <button
        onClick={() => onSelect(conv.id)}
        className={`w-full text-left px-2.5 py-2 rounded-lg text-[13px] transition-colors leading-snug flex items-center gap-2 ${
          active
            ? "bg-secondary text-foreground"
            : "text-[hsl(var(--sidebar-foreground))] hover:bg-secondary/50 hover:text-foreground"
        }`}
      >
        {conv.pinned && <Star className="h-3 w-3 text-primary shrink-0 fill-primary" />}
        <span className="truncate flex-1">{conv.title}</span>
      </button>

      {/* Three-dot trigger */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(isMenuOpen ? null : conv.id);
        }}
        className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground transition-all ${
          isMenuOpen ? "opacity-100 bg-secondary" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {/* Context menu */}
      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(null)} />
          <div className="absolute right-0 top-full mt-1 z-50 w-48 rounded-xl border border-border bg-popover p-1 shadow-xl shadow-black/40 animate-fade-in">
            <MenuItem
              icon={Star}
              label={conv.pinned ? "Remove from Starred" : "Add to Starred"}
              onClick={() => { onPin(conv.id); setMenuOpen(null); }}
            />
            <MenuItem
              icon={Edit3}
              label="Rename"
              onClick={() => { setRenameValue(conv.title); setRenaming(conv.id); setMenuOpen(null); }}
            />
            <MenuItem
              icon={Share}
              label="Share"
              onClick={() => { toast("Share feature coming soon"); setMenuOpen(null); }}
            />
            {onArchive && (
              <MenuItem
                icon={Archive}
                label="Archive"
                onClick={() => { onArchive(conv.id); setMenuOpen(null); }}
              />
            )}
            <MenuItem
              icon={Download}
              label="Download"
              onClick={() => { toast("Download feature coming soon"); setMenuOpen(null); }}
            />
            <div className="my-1 mx-2 h-px bg-border/60" />
            <MenuItem
              icon={Trash2}
              label="Delete"
              danger
              onClick={() => { onDelete(conv.id); setMenuOpen(null); }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({
  icon: Icon,
  label,
  danger,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
        danger
          ? "text-destructive hover:bg-destructive/10"
          : "text-foreground hover:bg-secondary/60"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
